import { Analytics } from '@segment/analytics-next';

const ANONYMOUS_ID_KEY = 'sticker_app_anonymous_id';

// Get or create anonymous ID
export const getAnonymousId = () => {
  let anonymousId = localStorage.getItem(ANONYMOUS_ID_KEY);
  if (!anonymousId) {
    anonymousId = crypto.randomUUID();
    localStorage.setItem(ANONYMOUS_ID_KEY, anonymousId);
  }
  return anonymousId;
};

export interface StickerDesignTrackingProps {
  width: number;
  height: number;
  shape: string;
  finish: 'matte' | 'glossy';
  quantity: number;
  price: number;
}

export interface UserIdentifyProps {
  userId?: string;
  traits?: {
    email?: string;
    name?: string;
    [key: string]: any;
  };
}

// Updated event names to follow Object-Action framework
export const trackEvents = {
  // Design events
  DESIGN_CREATED: 'Design Created',
  DESIGN_UPDATED: 'Design Updated',
  DESIGN_RENAMED: 'Design Renamed',
  DESIGN_DOWNLOADED: 'Design Downloaded',
  DESIGN_RESHAPED: 'Design Reshaped',
  DESIGN_RESIZED: 'Design Resized',
  DESIGN_REFINISHED: 'Design Refinished',
  DESIGN_RESET: 'Design Reset',
  DESIGN_EDITED: 'Design Edited',
  
  // Element events
  ELEMENT_ADDED: 'Element Added',
  ELEMENT_REMOVED: 'Element Removed',
  ELEMENT_MODIFIED: 'Element Modified',
  
  // Order events
  ORDER_QUANTITY_CHANGED: 'Order Quantity Changed',
  
  // Checkout events
  CHECKOUT_STARTED: 'Checkout Started',
  CHECKOUT_STEP_COMPLETED: 'Checkout Step Completed',
  CHECKOUT_COMPLETED: 'Checkout Completed',
  CHECKOUT_ABANDONED: 'Checkout Abandoned',
  
  // Page events
  PAGE_VIEWED: 'Page Viewed',
  
  // Error events
  ERROR_OCCURRED: 'Error Occurred'
} as const;

// Property names should use snake_case
interface BaseTrackingProps {
  // Design properties
  design_id?: string;
  design_name?: string;
  width?: number;
  height?: number;
  shape?: string;
  finish?: string;
  background_color?: string;
  
  // Order properties
  quantity?: number;
  price?: number;
  price_per_sticker?: number;
  
  // Element properties
  element_id?: string;
  element_type?: string;
  
  // Page properties
  page_name?: string;
  page_path?: string;
  page_url?: string;
  
  // Error properties
  error_message?: string;
  error_type?: string;
  
  // Additional properties
  [key: string]: any;
}

export const trackStickerDesign = async (
  analytics: Analytics | null,
  event: string,
  properties: BaseTrackingProps
) => {
  if (!analytics) {
    console.warn('Analytics instance not initialized');
    return;
  }

  try {
    const anonymousId = getAnonymousId();
    const baseProperties = {
      timestamp: new Date().toISOString(),
      anonymous_id: anonymousId,
      ...properties
    };

    await analytics.track(event, baseProperties);
  } catch (error) {
    console.error('Failed to track event:', event, error);
  }
};

export const trackPageView = async (
  analytics: Analytics | null,
  pageName: string,
  properties?: Omit<BaseTrackingProps, 'page_name'>
) => {
  if (!analytics) {
    console.warn('Analytics instance not initialized');
    return;
  }

  try {
    const anonymousId = getAnonymousId();
    const baseProperties = {
      page_name: pageName,
      page_path: window.location.pathname,
      page_url: window.location.href,
      page_title: document.title,
      timestamp: new Date().toISOString(),
      anonymous_id: anonymousId,
      ...properties
    };

    // Track page view as both page and track calls
    await analytics.page(pageName, baseProperties);
    await analytics.track(trackEvents.PAGE_VIEWED, baseProperties);
  } catch (error) {
    console.error('Failed to track page view:', pageName, error);
  }
};

export const identifyUser = async (
  analytics: Analytics | null,
  { userId, traits }: UserIdentifyProps
) => {
  if (!analytics) {
    console.warn('Analytics instance not initialized');
    return;
  }

  if (!userId) {
    console.warn('User ID is required for identification');
    return;
  }

  try {
    const anonymousId = getAnonymousId();
    await analytics.identify(userId, {
      anonymous_id: anonymousId,
      ...traits,
      updated_at: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Failed to identify user:', userId, error);
  }
};

export const trackError = async (
  analytics: Analytics | null,
  error: Error,
  additionalContext?: Record<string, any>
) => {
  if (!analytics) {
    console.warn('Analytics instance not initialized');
    return;
  }

  try {
    const anonymousId = getAnonymousId();
    const properties = {
      error_type: error.name,
      error_message: error.message,
      error_stack: error.stack,
      page_url: window.location.href,
      timestamp: new Date().toISOString(),
      anonymous_id: anonymousId,
      ...additionalContext
    };

    await analytics.track(trackEvents.ERROR_OCCURRED, properties);
  } catch (trackError) {
    console.error('Failed to track error event:', trackError);
  }
};
